import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [selectedChapter, setSelectedChapter] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.slug !== comic.slug)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen">
        <div className="pt-24 pb-10 max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.category, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          <article className="flex flex-col lg:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0">
              <div className="w-full sm:w-[260px] md:w-[300px] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full aspect-[3/4] object-cover object-top"
                  title={`${comic.title} - 韩漫在线`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{comic.altTitle}</p>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                  </div>
                  <strong>{comic.rating}</strong>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {formatViews(comic.views)}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line"></i>
                  </div>
                  {formatViews(comic.favorites)}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mb-5">
                <div>
                  <span className="text-foreground-500">作者：</span>
                  <span className="text-foreground-800 font-medium">{comic.author}</span>
                </div>
                <div>
                  <span className="text-foreground-500">画师：</span>
                  <span className="text-foreground-800 font-medium">{comic.artist}</span>
                </div>
                <div>
                  <span className="text-foreground-500">分类：</span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {comic.category}
                  </Link>
                </div>
                <div>
                  <span className="text-foreground-500">总章节：</span>
                  <span className="text-foreground-800 font-medium">{comic.totalChapters}话</span>
                </div>
                <div>
                  <span className="text-foreground-500">最近更新：</span>
                  <span className="text-foreground-800 font-medium">{comic.updatedAt}</span>
                </div>
                <div>
                  <span className="text-foreground-500">最新：</span>
                  <span className="text-foreground-800 font-medium truncate block">{comic.latestChapter}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mb-6">
                <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2">作品简介</h2>
                <div className="text-sm text-foreground-600 leading-relaxed">
                  {showFullDescription ? comic.description : (
                    <>
                      {comic.description.slice(0, 120)}
                      {comic.description.length > 120 && (
                        <>
                          ...
                          <button
                            onClick={() => setShowFullDescription(true)}
                            className="text-primary-500 hover:text-primary-600 ml-1 cursor-pointer whitespace-nowrap"
                          >
                            展开全部
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {showFullDescription && (
                    <button
                      onClick={() => setShowFullDescription(false)}
                      className="text-primary-500 hover:text-primary-600 ml-1 cursor-pointer whitespace-nowrap"
                    >
                      收起
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 border border-background-300 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                    收藏
                  </span>
                </button>
              </div>
            </div>
          </article>

          <section className="mb-10">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">章节列表</h2>
            <div className="border border-background-200 rounded-xl overflow-hidden divide-y divide-background-100">
              {comic.chapters.map((chapter, idx) => (
                <button
                  key={chapter.number}
                  onClick={() => setSelectedChapter(idx)}
                  className={`w-full flex items-center justify-between px-4 md:px-6 py-3.5 text-left transition-colors cursor-pointer ${
                    selectedChapter === idx
                      ? 'bg-primary-50'
                      : 'hover:bg-background-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      selectedChapter === idx
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600'
                    }`}>
                      {chapter.number}
                    </span>
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                      第{chapter.number}话 - {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updatedAt}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              阅读预览 - 第{comic.chapters[selectedChapter]?.number || 1}话
            </h2>
            <div className="max-w-[800px] mx-auto space-y-2">
              {comic.chapters[selectedChapter]?.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${comic.title} 第${comic.chapters[selectedChapter].number}话 第${i + 1}页`}
                  className="w-full rounded-lg"
                  title={`${comic.title} - 第${comic.chapters[selectedChapter].number}话`}
                />
              ))}
            </div>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => selectedChapter > 0 && setSelectedChapter(selectedChapter - 1)}
                disabled={selectedChapter <= 0}
                className="px-5 py-2 border border-background-300 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </div>
                  上一话
                </span>
              </button>
              <button
                onClick={() => selectedChapter < comic.chapters.length - 1 && setSelectedChapter(selectedChapter + 1)}
                disabled={selectedChapter >= comic.chapters.length - 1}
                className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  下一话
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </span>
              </button>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="border-t border-background-200 pt-10">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                更多{comic.category}漫画
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {relatedComics.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/comic/${c.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                      <img
                        src={c.coverImage}
                        alt={c.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={c.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-2.5">
                        <h3 className="text-xs font-semibold text-white leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                          {c.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩漫在线`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
            dateModified: comic.updatedAt,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hanmanonline.com/comic/${comic.slug}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
                { '@type': 'ListItem', position: 2, name: comic.category, item: `https://hanmanonline.com/category/${comic.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: comic.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}